import React, { useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import CustomReactSelect from "components/CustomReactSelect";
import useGlobalStore from "store/useGlobalStore";
import { getSparepartTags } from "api/inventorySpareparts";

const SparePartTagSelector = ({
    value,
    onChange,
    label = "Spare Part Tag",
    placeholder = "Select Tag...",
    isMulti = false,
    required = false,
    disabled = false,
    creatable = false,
    ...props
}) => {
    const { sparePartTags, setSparePartTags } = useGlobalStore();

    const handleFetch = useCallback(async () => {
        try {
            const response = await getSparepartTags();
            const data = response.data ?? [];
            const dataOptions = data.map(item => ({
                label: item,
                value: item
            }));
            setSparePartTags(dataOptions);
            return dataOptions;
        } catch (error) {
            return [];
        }
    }, [setSparePartTags]);

    useEffect(() => {
        if (sparePartTags.length === 0) {
            handleFetch();
        }
    }, [sparePartTags.length, handleFetch]);

    const getSelectedValue = () => {
        if (!value) return null;
        if (isMulti) {
            return sparePartTags.filter(option => value.includes(option.value)) || null;
        }
        return sparePartTags.find(option => option.value === value) || null;
    };

    const handleChange = (selectedOption) => {
        if (isMulti) {
            const selectedValues = selectedOption ? selectedOption.map(opt => opt.value) : [];
            if (onChange) {
                onChange(selectedValues, selectedOption);
            }
        } else {
            const selectedValue = selectedOption ? selectedOption.value : null;
            if (onChange) {
                onChange(selectedValue, selectedOption);
            }
        }
    };

    const handleCreate = (inputValue) => {
        const newOption = {
            label: inputValue,
            value: inputValue
        };
        
        // Add the new option to the global store
        setSparePartTags([...sparePartTags, newOption]);
        
        if (isMulti) {
            const currentValues = value || [];
            const newValues = [...currentValues, inputValue];
            if (onChange) {
                onChange(newValues, [...(getSelectedValue() || []), newOption]);
            }
        } else {
            if (onChange) {
                onChange(inputValue, newOption);
            }
        }
    };


    return (
        <CustomReactSelect
            label={label}
            isMulti={isMulti}
            isClearable
            cacheOptions={false}
            loadOptions={handleFetch}
            value={getSelectedValue()}
            options={sparePartTags}
            onChange={handleChange}
            placeholder={placeholder}
            required={required}
            disabled={disabled}
            noOptionsMessage={() => "No spare part tags found"}
            creatable={creatable}
            onCreateOption={creatable ? handleCreate : undefined}
            {...props}
        />
    );
};

SparePartTagSelector.propTypes = {
    value: PropTypes.oneOfType([
        PropTypes.array,
        PropTypes.object,
        PropTypes.string,
        PropTypes.number,
    ]),
    onChange: PropTypes.func.isRequired,
    label: PropTypes.string,
    placeholder: PropTypes.string,
    isMulti: PropTypes.bool,
    required: PropTypes.bool,
    disabled: PropTypes.bool,
    creatable: PropTypes.bool,
};

export default SparePartTagSelector;